import { DEFAULT_AGENT_SLUG, FALLBACK_AGENT_ROSTER, TheoAgentNav } from "./theoAgents";

export const THEO_OVERVIEW_PATH = "/theo-overview";

export interface SectionTabDef {
  id: string;
  label: string;
  to: string;
  /** Dashboard panels stay on the analytics route and only change `panel`. */
  kind?: "panel" | "route";
  match: (pathname: string, search: string) => boolean;
  /** Nested destinations, such as an agent's Monitor and Configure panels. */
  children?: SectionTabDef[];
  /** Semantic marker the sidebar maps to a glyph. Keeps nav defs free of JSX. */
  icon?: "add";
  /** When set, the sidebar shows the row greyed out and does not navigate. */
  disabledReason?: string;
}

export const PANEL_DASHBOARD = "dashboard";
export const PANEL_MY_FOCUS = "my-focus";
export const PANEL_TICKET_ANALYSIS = "ticket-analysis";
export const PANEL_USAGE_INSIGHTS = "usage-insights";
export const PANEL_DEVICES = "devices";
export const PANEL_TICKET_LIFECYCLE = "ticket-lifecycle";

const ANALYTICS_PATHS = new Set(["/msp-analytics", "/dashboard"]);

export function isAnalyticsPath(pathname: string): boolean {
  return ANALYTICS_PATHS.has(pathname);
}

export function currentAnalyticsPanel(search: string): string {
  return (new URLSearchParams(search).get("panel") || PANEL_DASHBOARD).trim().toLowerCase();
}

function panelTab(id: string, label: string): SectionTabDef {
  return {
    id,
    label,
    to: `/msp-analytics?panel=${id}`,
    kind: "panel",
    match: (pathname, search) => isAnalyticsPath(pathname) && currentAnalyticsPanel(search) === id,
  };
}

export interface InsightsTabAccess {
  /** Theo Overview and the analytics panels. LaunchDarkly `theoanalytics`, on unless turned off. */
  theoAnalytics: boolean;
  technician: boolean;
  ticketAnalysis: boolean;
  usageInsights: boolean;
  ticketLifecycle: boolean;
  showUsage: boolean;
}

export function buildInsightsTabs(access: InsightsTabAccess): SectionTabDef[] {
  const tabs: SectionTabDef[] = [];
  // Theo Overview is Insights' landing page; it replaced the old "MSP overview" dashboard panel.
  if (access.theoAnalytics) {
    tabs.push({
      id: "theo-overview",
      label: "Theo Overview",
      to: THEO_OVERVIEW_PATH,
      kind: "route",
      match: (pathname) => matchesPrefix(pathname, THEO_OVERVIEW_PATH),
    });
  }
  if (access.theoAnalytics && access.technician) {
    tabs.push(panelTab(PANEL_MY_FOCUS, "My focus"));
  }
  if (access.theoAnalytics && access.ticketAnalysis) {
    tabs.push(panelTab(PANEL_TICKET_ANALYSIS, "Ticket Analysis"));
  }
  if (access.theoAnalytics && access.usageInsights) {
    tabs.push(panelTab(PANEL_USAGE_INSIGHTS, "Usage Insights"));
  }
  tabs.push(panelTab(PANEL_DEVICES, "Devices"));
  if (access.ticketLifecycle) {
    tabs.push(panelTab(PANEL_TICKET_LIFECYCLE, "Ticket Lifecycle"));
  }
  if (access.showUsage) {
    tabs.push({
      id: "usage",
      label: "Usage",
      to: "/usage",
      kind: "route",
      match: (pathname) => pathname === "/usage" || pathname.startsWith("/usage/"),
    });
  }
  return tabs;
}

export function selectedTabId(tabs: SectionTabDef[], pathname: string, search: string): string | false {
  return tabs.find((tab) => tab.match(pathname, search))?.id ?? false;
}

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function playbooksTab(): SectionTabDef {
  return {
    id: "playbooks",
    label: "Playbooks",
    to: "/playbooks",
    match: (pathname) =>
      matchesPrefix(pathname, "/playbooks") || matchesPrefix(pathname, "/application-skills"),
  };
}

export function buildTheoTabs(access: {
  workflows: boolean;
  schedules: boolean;
  playbooks: boolean;
}): SectionTabDef[] {
  const tabs: SectionTabDef[] = [
    {
      id: "chat",
      label: "Chat Assistant",
      to: "/chat-assistant",
      match: (pathname) =>
        matchesPrefix(pathname, "/chat-assistant") || matchesPrefix(pathname, "/deep-research"),
    },
  ];
  if (access.workflows) {
    tabs.push({
      id: "workflows",
      label: "Workflows",
      to: "/custom_workflows",
      match: (pathname) => matchesPrefix(pathname, "/custom_workflows"),
    });
  }
  if (access.schedules) {
    tabs.push({
      id: "schedules",
      label: "Schedules",
      to: "/schedules",
      match: (pathname) => matchesPrefix(pathname, "/schedules"),
    });
  }
  if (access.playbooks) {
    tabs.push(playbooksTab());
  }
  return tabs;
}

export function buildAgentTabs(agents: TheoAgentNav[] = FALLBACK_AGENT_ROSTER): SectionTabDef[] {
  const roster: SectionTabDef[] = agents.map((agent) => {
    const monitor: SectionTabDef = {
      id: `${agent.id}-monitor`,
      label: "Monitor",
      to: agent.monitorTo,
      kind: "route",
      match: (pathname) => matchesPrefix(pathname, agent.monitorTo),
    };
    const configure: SectionTabDef = {
      id: `${agent.id}-configure`,
      label: "Configure",
      to: agent.configureTo,
      kind: "route",
      match: (pathname) =>
        matchesPrefix(pathname, agent.configureTo) ||
        (agent.id === DEFAULT_AGENT_SLUG && matchesPrefix(pathname, "/ai-automations")),
    };
    const children = [monitor, configure];
    return {
      id: agent.id,
      label: agent.label,
      to: agent.monitorTo,
      kind: "route" as const,
      match: (pathname, search) => children.some((child) => child.match(pathname, search)),
      children,
    };
  });

  // Sits after the roster: the action that adds to the list belongs below it.
  const create: SectionTabDef = {
    id: "create",
    label: "Create an Agent",
    to: "/agents/new",
    kind: "route",
    icon: "add",
    match: (pathname) => matchesPrefix(pathname, "/agents/new"),
  };
  return [...roster, create];
}

export function buildAdministrationTabs(access: { playbooks: boolean; billing: boolean }): SectionTabDef[] {
  const tabs: SectionTabDef[] = [
    {
      id: "customers",
      label: "PSA Customers",
      to: "/accounts",
      match: (pathname) => pathname === "/accounts" || pathname.startsWith("/accounts/"),
    },
    {
      id: "users",
      label: "Theo Users",
      to: "/theo-users",
      match: (pathname) => pathname === "/theo-users" || pathname.startsWith("/theo-users/"),
    },
    {
      id: "integrations",
      label: "Integrations",
      to: "/integrations",
      match: (pathname) =>
        pathname === "/integrations" ||
        pathname.startsWith("/integrations/") ||
        pathname.startsWith("/app-configuration/") ||
        pathname.startsWith("/platform-configuration/") ||
        pathname === "/configuration",
    },
  ];
  if (access.playbooks) {
    tabs.push(playbooksTab());
  }
  if (access.billing) {
    tabs.push({
      id: "billing",
      label: "Billing",
      to: "/billing",
      match: (pathname) => pathname === "/billing" || pathname.startsWith("/billing/"),
    });
  }
  return tabs;
}

export function buildDeveloperTabs(access: { observer: boolean }): SectionTabDef[] {
  const tabs: SectionTabDef[] = [];
  if (access.observer) {
    tabs.push(
      {
        id: "threads",
        label: "AI Thread Observer",
        to: "/observer",
        match: (pathname, search) => {
          if (pathname !== "/observer") return false;
          const tab = new URLSearchParams(search).get("tab");
          return !tab || tab === "threads";
        },
      },
      {
        id: "rag",
        label: "RAG Observer",
        to: "/observer?tab=rag",
        match: (pathname, search) => pathname === "/observer" && new URLSearchParams(search).get("tab") === "rag",
      },
      {
        id: "activity",
        label: "Activity Report",
        to: "/observer?tab=activity",
        match: (pathname, search) => {
          if (pathname !== "/observer") return false;
          const tab = new URLSearchParams(search).get("tab");
          return tab === "activity" || tab === "digest";
        },
      },
      {
        id: "quality",
        label: "Usage Quality",
        to: "/observer?tab=quality",
        match: (pathname, search) => pathname === "/observer" && new URLSearchParams(search).get("tab") === "quality",
      },
      {
        id: "change-log",
        label: "Change Log",
        to: "/observer/change-log",
        match: (pathname) => pathname === "/observer/change-log",
      }
    );
  }
  return tabs;
}

export function buildHelpTabs(access: { auditLogs: boolean }): SectionTabDef[] {
  const tabs: SectionTabDef[] = [
    {
      id: "documentation",
      label: "Documentation",
      to: "/help-center/documentation",
      match: (pathname) =>
        pathname === "/help-center/documentation" ||
        pathname.startsWith("/help-center/documentation/") ||
        pathname === "/documentation" ||
        pathname.startsWith("/documentation/"),
    },
    {
      id: "guides",
      label: "Guides",
      to: "/help-center/guides",
      match: (pathname) => pathname === "/help-center/guides" || pathname.startsWith("/help-center/guides/"),
    },
    {
      id: "release-notes",
      label: "Release Notes",
      to: "/help-center/release-notes",
      match: (pathname) => pathname === "/help-center/release-notes",
    },
  ];
  if (access.auditLogs) {
    tabs.push({
      id: "logs",
      label: "Logs",
      to: "/audit-logs",
      match: (pathname) => pathname === "/audit-logs",
    });
  }
  return tabs;
}
